import { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Zap, Check, Crown, Loader2, Clock, MapPin, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGeo } from "@/context/GeoContext";
import { trackEvent } from "@/lib/posthog";
import { toast } from "sonner";

const LIMITLESS = "limitless_30d";

function formatINR(paise) { return "₹" + (paise / 100).toLocaleString("en-IN"); }
function formatUSD(cents) { return "$" + (cents / 100).toFixed(2); }

/**
 * Resolve when window.Razorpay is available. The <script src="checkout.razorpay.com/v1/checkout.js">
 * is synchronous in index.html but slow networks / content-blockers can delay
 * it, so we poll for up to 5s. Per V1.1 GOLD directive, if the first 5s
 * window times out we re-inject the SDK <script> tag and try ONE more time
 * for another 5s — total 10s ceiling, single retry, then we surface a
 * deterministic toast instead of the dreaded "Razorpay SDK not loaded"
 * crash. This eliminates the "Something went wrong" overlay caused by
 * options being constructed against an uninitialised SDK.
 */
const RAZORPAY_SRC = "https://checkout.razorpay.com/v1/checkout.js";

function _pollRazorpay(maxWaitMs, pollMs = 150) {
  return new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && window.Razorpay) return resolve(window.Razorpay);
    const started = Date.now();
    const timer = setInterval(() => {
      if (window.Razorpay) { clearInterval(timer); resolve(window.Razorpay); return; }
      if (Date.now() - started > maxWaitMs) { clearInterval(timer); reject(new Error("timeout")); }
    }, pollMs);
  });
}

function _injectRazorpayScript() {
  return new Promise((resolve, reject) => {
    // If a tag is already on the page from index.html, just resolve — the
    // browser is already trying to fetch/execute it.
    const existing = document.querySelector(`script[src="${RAZORPAY_SRC}"]`);
    if (existing) return resolve();
    const s = document.createElement("script");
    s.src = RAZORPAY_SRC;
    s.async = true;
    // NOTE: do NOT set `crossOrigin = "anonymous"` here. The Razorpay
    // checkout.js endpoint does not return Access-Control-Allow-Origin,
    // so a CORS-attributed <script> tag is rejected by the browser
    // (net::ERR_FAILED + console "blocked by CORS policy"), which would
    // make the V1.1 GOLD 5s-retry path always fail with "Razorpay SDK
    // did not load" — defeating the very purpose of the retry. The
    // <script> tag in public/index.html is loaded without crossorigin
    // and works; we mirror that here.
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("razorpay script load error"));
    document.head.appendChild(s);
  });
}

async function waitForRazorpay() {
  // Attempt 1 — fast path: the SDK is usually already on the page.
  try { return await _pollRazorpay(5000); } catch {}
  // Attempt 2 — re-inject and try again. Fixes ad-blockers that swallowed
  // the original tag, or networks where the first GET timed out.
  try { await _injectRazorpayScript(); } catch {}
  try { return await _pollRazorpay(5000); } catch {}
  throw new Error("Razorpay SDK did not load — disable ad-blockers or check your network and try again.");
}

/** Single source of truth for the 4-tier upsell. As of 2026-04-28 the
 *  international rail switched from "Razorpay-USD-display" to a real
 *  Gumroad checkout. Logic:
 *    • is_india === true (or manual INR override) → Razorpay (INR)
 *    • Otherwise → Gumroad overlay opened against `${gumroad_url}?
 *      variant=<Tier>&email=…&user_id=…` — Gumroad's Lightbox script
 *      reads the `?variant=` query string and pre-selects the matching
 *      tier so the price the user clicks is the price they're charged.
 *    • Currency toggle remains so NRIs / VPN users can override either
 *      direction. */
export default function PricingModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const geo = useGeo(); // { country, is_india, currency, source, setOverride, override, loading }
  const [catalog, setCatalog] = useState({ packs: [], razorpay_key_id: "", gumroad_url: "" });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  // inFlightRef locks the pack id once an order has been requested so that
  // re-renders, geo override toggles, /pricing refetches, or rapid clicks
  // can't shuffle `selected` to a different pack mid-checkout.
  const inFlightRef = useRef(null);
  // rzInstanceRef holds the Razorpay Checkout instance so we can explicitly
  // close it during cleanup and stop the SDK's lingering DOM/focus ghosts.
  const rzInstanceRef = useRef(null);

  /** Single cleanup point — called from success, dismiss, and every failure
   *  branch. Without this the previous attempt's `selected`/`loading` state
   *  bled into the next click ("ghost" pack lit up, button stuck disabled).
   *  We do NOT call rz.close() from here — Razorpay owns its own overlay
   *  lifecycle (modal.ondismiss + handler), and force-closing it from React
   *  was tearing down the overlay we'd just handed control to. */
  const resetCheckout = useCallback(() => {
    inFlightRef.current = null;
    setLoading(false);
    setSelected(null);
    rzInstanceRef.current = null;
  }, []);

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setCatalog(data)).catch(() => {});
  }, []);

  // Fire upgrade_clicked once when the modal opens. Intentionally NOT
  // depending on catalog.packs / geo.* — those refire would (a) double-count
  // the analytics event and (b) re-warm the SDK every geo toggle. We only
  // care about the open-edge.
  useEffect(() => {
    if (!open) return;
    trackEvent("upgrade_clicked", {
      geo_country: geo.country || null,
      geo_currency: geo.currency,
      is_india: geo.is_india,
      geo_source: geo.source,
      catalog_loaded: (catalog.packs || []).length,
    });
    // Warm the Razorpay SDK eagerly so the first click is instant.
    waitForRazorpay().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When the modal is closed externally (esc, backdrop, success), make sure
  // any in-flight selection state is wiped — otherwise re-opening the modal
  // shows a stale "selected" highlight on the previous pack.
  // GUARD: skip cleanup when inFlightRef is set. That means the dialog was
  // closed deliberately by handleRazorpay() to hand focus to the Razorpay
  // overlay; Razorpay's own handler / ondismiss will call resetCheckout()
  // when the user finishes or cancels. Without this guard the Razorpay
  // overlay would be torn down 30ms after opening (iter13 regression).
  useEffect(() => {
    if (!open && !inFlightRef.current) resetCheckout();
  }, [open, resetCheckout]);

  const useINR = geo.is_india === true;

  const handleRazorpay = useCallback(async (packId) => {
    // Re-entrancy guard — if a checkout is already in flight, ignore subsequent
    // clicks regardless of which pack the user hits. Prevents the "₹99 jumps
    // to ₹1999" race where a stray double-click on a different card overwrites
    // selected/order state mid-flight.
    if (inFlightRef.current) return;
    inFlightRef.current = packId;
    setLoading(true);
    setSelected(packId);
    try {
      const { data: order } = await api.post("/payments/razorpay/pack-order", { pack_id: packId });
      // If the user dismissed/closed our modal while the order was being
      // created, abort cleanly — don't open Razorpay against a stale state.
      if (inFlightRef.current !== packId) return;

      // Wait until window.Razorpay is available — handles slow SDKs / blockers.
      let RzCtor;
      try {
        RzCtor = await waitForRazorpay();
      } catch (sdkErr) {
        toast.error(sdkErr.message || "Razorpay SDK not loaded");
        resetCheckout();
        return;
      }
      const rz = new RzCtor({
        key: order.key_id,
        amount: order.amount,
        currency: "INR",
        name: "Oak AI Campus",
        description: order.description,
        order_id: order.order_ref,
        prefill: { name: user?.name || "", email: user?.email || "" },
        notes: { user_id: user?.user_id, pack_id: packId },
        theme: { color: "#0044FF" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/confirm", {
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_order_id: resp.razorpay_order_id || order.order_ref,
              razorpay_signature: resp.razorpay_signature,
              pack_id: packId,
            });
            const p = (catalog.packs || []).find((x) => x.id === packId);
            trackEvent("pack_purchased", {
              pack_id: packId,
              pack_type: packId === LIMITLESS ? "limitless" : "credits",
              provider: "razorpay",
              geo_country: geo?.country || "IN",
              currency: "INR",
              inr_paise: p?.inr_paise || null,
              credits: p?.credits || null,
              payment_id: resp.razorpay_payment_id,
            });
            if (packId === LIMITLESS) {
              trackEvent("limitless_activated", { provider: "razorpay", geo_country: geo?.country || "IN" });
            }
            toast.success("Payment received — credits updated.");
            resetCheckout();
            onOpenChange(false);
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed");
            resetCheckout();
          }
        },
        modal: { ondismiss: () => { resetCheckout(); } },
      });
      rzInstanceRef.current = rz;
      // Close our PricingModal BEFORE opening Razorpay's overlay. Radix's
      // Dialog focus-trap stays active while the modal is mounted, which is
      // what was eating focus on the Razorpay mobile-number / OTP inputs
      // (user could see the cursor but typing did nothing). Closing our
      // dialog hands focus management entirely to Razorpay's iframe.
      onOpenChange(false);
      rz.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Razorpay failed");
      resetCheckout();
    }
  }, [user, onOpenChange, catalog.packs, geo, resetCheckout]);

  const handleGumroad = useCallback((pack) => {
    // Gumroad opens in a new tab — their Lightbox JS would also work but
    // would require loading their gumroad.com/js/gumroad.js script and
    // navigating via window.location.href which fights React-Router. The
    // simpler, more reliable pattern is `window.open(url, '_blank')`.
    //
    // The `?variant=<Tier>` query string is read by Gumroad's checkout
    // page and pre-selects the matching tier so the user can't accidentally
    // pick a different one. We also pass `email` (prefill) and `user_id`
    // (for our webhook to attribute the sale instantly without an email
    // lookup race for new accounts).
    const baseUrl = catalog.gumroad_url || "https://oakacademy.gumroad.com/l/bojein";
    const variant = pack?.gumroad_variant;
    if (!variant) {
      toast.error("This pack is not yet available internationally.");
      return;
    }
    const params = new URLSearchParams({
      variant,
      wanted: "true",  // forces Gumroad to skip the product page → straight to checkout
    });
    if (user?.email) params.set("email", user.email);
    if (user?.user_id) params.set("user_id", user.user_id);
    const url = `${baseUrl}?${params.toString()}`;
    trackEvent("upgrade_gumroad_clicked", {
      pack_id: pack.id,
      variant,
      usd_cents: pack.usd_cents,
      geo_country: geo?.country || null,
    });
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Opening secure Gumroad checkout in a new tab…");
    // Close the modal — the user is now in Gumroad's overlay. Once they
    // finish, our webhook (POST /api/payments/gumroad-webhook) credits
    // them automatically; no client-side polling needed.
    onOpenChange(false);
  }, [catalog.gumroad_url, user, geo, onOpenChange]);

  const handlePurchase = (packId) => {
    const pack = (catalog.packs || []).find((p) => p.id === packId);
    if (useINR) return handleRazorpay(packId);
    return handleGumroad(pack);
  };

  const packs = catalog.packs || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-white/10 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="pricing-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Zap size={20} className="text-[#ffb067]" />
            Unlock more learning
          </DialogTitle>
          <DialogDescription className="text-white/60 text-sm">
            {useINR ? (
              <>Pay in <strong>INR</strong> via Razorpay · all cards, UPI, NetBanking</>
            ) : (
              <>Pay in <strong>USD</strong> via Gumroad · global cards, Apple Pay, PayPal</>
            )}
            {geo.country && (
              <span className="inline-flex items-center gap-1 ml-2 text-[10px] text-white/40 font-mono-oak align-middle">
                <MapPin size={10} /> detected {geo.country}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Manual currency toggle — always visible so NRIs on non-IN IPs can
            pick INR, and foreign students using an Indian VPN can pick USD. */}
        <div
          role="tablist"
          className="mt-3 inline-flex rounded-full border border-white/10 bg-white/[0.03] p-0.5 self-start"
          data-testid="currency-toggle"
        >
          <button
            role="tab"
            aria-selected={useINR}
            onClick={() => geo.setOverride("IN")}
            className={`px-3 py-1.5 rounded-full text-xs font-display font-semibold transition ${
              useINR ? "bg-[#00FF9D]/15 text-[#00FF9D]" : "text-white/60 hover:text-white"
            }`}
            data-testid="currency-toggle-inr"
          >
            ₹ INR · Razorpay
          </button>
          <button
            role="tab"
            aria-selected={!useINR}
            onClick={() => geo.setOverride("US")}
            className={`px-3 py-1.5 rounded-full text-xs font-display font-semibold transition ${
              !useINR ? "bg-[#8aa8ff]/15 text-[#8aa8ff]" : "text-white/60 hover:text-white"
            }`}
            data-testid="currency-toggle-usd"
          >
            $ USD · Gumroad
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3" data-testid="pricing-grid">
          {packs.map((p) => {
            const isLimitless = p.id === LIMITLESS;
            const isSelected = selected === p.id;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => handlePurchase(p.id)}
                disabled={loading}
                className={`text-left rounded-xl p-4 border transition flex flex-col gap-1.5 group disabled:opacity-50 ${
                  isLimitless
                    ? "border-[#ffb067]/40 bg-gradient-to-br from-[#FF7B00]/10 via-[#5D3FD3]/10 to-[#0044FF]/10 hover:border-[#ffb067]/70"
                    : "border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-white/25"
                }`}
                data-testid={`pricing-pack-${p.id}`}
              >
                <div className="flex items-center gap-1.5 label-eyebrow">
                  {isLimitless ? <Crown size={12} className="text-[#ffb067]" /> : <Check size={12} className="text-[#00FF9D]" />}
                  {p.title}
                </div>
                <div className="font-display text-2xl tracking-tight">
                  {useINR ? formatINR(p.inr_paise) : formatUSD(p.usd_cents)}
                </div>
                <div className="text-sm text-white/60">
                  {isLimitless ? (
                    <span className="inline-flex items-center gap-1">
                      <Clock size={12} /> 6 months · unlimited AI
                    </span>
                  ) : (
                    <>{(useINR ? p.credits : (p.gumroad_credits ?? p.credits))} credits</>
                  )}
                </div>
                {p.blurb && !isLimitless ? <div className="text-[11px] text-white/45 font-mono-oak mt-0.5">{p.blurb}</div> : null}
                <div className="mt-2 text-[11px] inline-flex items-center gap-1 text-white/60 group-hover:text-white">
                  {isSelected && loading ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : useINR ? (
                    <Zap size={12} />
                  ) : (
                    <ExternalLink size={12} />
                  )}
                  {useINR ? "Pay with Razorpay" : "Pay with Gumroad"}
                </div>
              </button>
            );
          })}
        </div>

        <div className="text-[10px] text-center text-white/40 mt-3">
          {useINR
            ? "Razorpay accepts UPI · Cards · NetBanking · Wallets. Toggle currency above to switch to USD via Gumroad."
            : "Gumroad accepts global Visa · Mastercard · Amex · Apple Pay · PayPal. Switch to ₹ INR for Indian payment methods."}
        </div>
      </DialogContent>
    </Dialog>
  );
}
